import { useState, useEffect, useRef, useCallback } from 'react';
import {
  searchAll,
  searchAllUniversal,
  searchKrollFallback,
  GlobalSearchResults,
} from '../../services/globalSearchService';
import api from '../../services/api';

const EMPTY_RESULTS: GlobalSearchResults = { orders: [], patients: [], subscriptions: [] };

/**
 * PRX-15: an Rx number as typed in the pharmacy system — digits only, 5–10
 * long (Fillware zero-pads to 9, Kroll numbers are 6–7). Such a query is
 * also resolved directly in the PMS, so this palette replaces the separate
 * "Search Rx" dialog: one box finds a patient, an order, or a prescription.
 */
export function isRxNumberLikeQuery(query: string): boolean {
  return /^\d{5,10}$/.test(query.trim());
}

export interface PmsRxHit {
  rxNumber: string;
  drugName: string;
  strength: string;
  patientName: string;
  compositePatientId: string;
}

async function lookupPmsRx(pharmacyId: string, rxNumber: string, signal: AbortSignal): Promise<PmsRxHit | null> {
  try {
    const response = await api.get(`/api/rx/${pharmacyId}/hardcopy/${rxNumber}`, { signal });
    const data = response.data?.data;
    if (!response.data?.success || !data?.patientId) return null;
    const fallbackName = [data.patientLastName, data.patientFirstName].filter(Boolean).join(', ');
    return {
      rxNumber: String(data.rxNumber ?? rxNumber),
      drugName: data.brandName || data.genericName || 'Unknown drug',
      strength: data.drugStrength || '',
      patientName: data.patientName || fallbackName || 'Unknown patient',
      compositePatientId: `${pharmacyId}_${data.patientId}`,
    };
  } catch {
    // 404 (no such Rx) or aborted — nothing to show; the PHOX results stand on their own.
    return null;
  }
}

/**
 * Whether a query is "name-like" enough to justify the Kroll patient fallback.
 * Mirrors the backend guard — fallback fires for plausible patient-name
 * searches.
 */
function isNameLikeQuery(query: string): boolean {
  const letters = (query.match(/[a-z]/gi) || []).length;
  const digits = (query.match(/\d/g) || []).length;
  return letters >= 2 && letters >= digits;
}

/**
 * Whether a query is "phone-like" — >= 7 digits after stripping "(", ")",
 * "-", "+" and whitespace. Same threshold the backend uses to decide a
 * query should be treated as a phone number.
 */
function isPhoneLikeQuery(query: string): boolean {
  const digits = query.replace(/\D/g, '');
  return digits.length >= 7;
}

/**
 * Whether a query looks like a unique cross-pharmacy identifier — an order
 * number (ORD-…), an Rx number (5+ contiguous digits), or a phone number.
 * Patient names belong to one pharmacy, but these identifiers are unique
 * across the network, so a search for one should auto-broaden so staff can
 * find the patient regardless of which pharmacy they're registered with.
 * Name queries stay pharmacy-scoped (privacy + relevance).
 */
function isIdentifierLikeQuery(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return false;
  // Order number: ORD-… (also covers TS-ORD-…)
  if (/^(ts-)?ord-/i.test(trimmed)) return true;
  // Phone-like
  if (isPhoneLikeQuery(trimmed)) return true;
  // Rx number / numeric identifier: 5+ digits, mostly numeric. Exclude
  // queries that are mostly letters (those are names with a digit).
  const letters = (trimmed.match(/[a-z]/gi) || []).length;
  const digits = (trimmed.match(/\d/g) || []).length;
  if (digits >= 5 && digits > letters) return true;
  return false;
}

interface UseGlobalSearchOptions {
  /**
   * When true, hits the cross-pharmacy aggregator endpoint instead of the
   * pharmacy-scoped one. Caller is responsible for only enabling this when
   * the user has `canSearchAcrossPharmacies`.
   */
  crossPharmacy?: boolean;
  /**
   * When true, identifier-like queries (order numbers, Rx numbers, phone
   * numbers) auto-broaden to the cross-pharmacy aggregator even if the
   * user hasn't flipped the toggle. Set this only when the caller has
   * already confirmed the user has `canSearchAcrossPharmacies` permission;
   * the backend still enforces, but this avoids a wasted request for users
   * without access.
   */
  autoBroadenIdentifierQueries?: boolean;
  debounceMs?: number;
}

/**
 * Global search hook. Default behaviour: pharmacy-scoped, requires a
 * `pharmacyId`. With `crossPharmacy: true`, ignores pharmacyId and hits the
 * universal aggregator (GP-314).
 */
export function useGlobalSearch(
  pharmacyId: string | null,
  query: string,
  options: UseGlobalSearchOptions = {},
) {
  const {
    crossPharmacy: crossPharmacyToggle = false,
    autoBroadenIdentifierQueries = false,
    debounceMs = 300,
  } = options;
  // Identifier-like queries auto-broaden when the caller has confirmed the
  // user has cross-pharmacy permission — staff don't need to remember to
  // flip the toggle when typing an Rx / order / phone number.
  const crossPharmacy =
    crossPharmacyToggle ||
    (autoBroadenIdentifierQueries && isIdentifierLikeQuery(query));
  const [results, setResults] = useState<GlobalSearchResults>(EMPTY_RESULTS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kroll fallback (pharmacy-scoped only): a second-phase MQTT lookup that runs
  // when the DB search lands with no patient. `Loading` drives a "checking
  // Kroll" hint; `Active` marks that the shown patients came from Kroll.
  const [krollFallbackLoading, setKrollFallbackLoading] = useState(false);
  const [krollFallbackActive, setKrollFallbackActive] = useState(false);
  const [pmsRx, setPmsRx] = useState<PmsRxHit | null>(null);
  const [pmsRxLoading, setPmsRxLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    setResults(EMPTY_RESULTS);
    setIsLoading(false);
    setError(null);
    setKrollFallbackLoading(false);
    setKrollFallbackActive(false);
    setPmsRx(null);
    setPmsRxLoading(false);
  }, []);

  useEffect(() => {
    // Cancel any pending timer
    if (timerRef.current) clearTimeout(timerRef.current);

    // Clear stale request
    if (abortRef.current) abortRef.current.abort();

    // Cross-pharmacy mode doesn't need a pharmacyId; pharmacy-scoped does.
    const hasContext = crossPharmacy || !!pharmacyId;
    if (!hasContext || !query || query.length < 2) {
      reset();
      return;
    }

    setIsLoading(true);
    setError(null);
    setKrollFallbackActive(false);
    // PRX-64: reset per cycle like `krollFallbackActive` above. Without this,
    // a cycle whose fallback request gets superseded (aborted) before it
    // resolves leaves this stuck `true` forever — the abort-guarded `finally`
    // below is the one place that would otherwise clear it, and an aborted
    // cycle never reaches it. The next cycle then shows "checking <PMS>…"
    // even when nothing is in flight, or (if it also needs the fallback)
    // shows it continuously across two searches instead of once per search.
    setKrollFallbackLoading(false);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      // PRX-15: resolve an Rx number in the pharmacy system in parallel with
      // the PHOX search, so a prescription with no PHOX order yet still shows up.
      if (pharmacyId && isRxNumberLikeQuery(query)) {
        setPmsRxLoading(true);
        void lookupPmsRx(pharmacyId, query.trim(), controller.signal).then((hit) => {
          if (controller.signal.aborted) return;
          setPmsRx(hit);
          setPmsRxLoading(false);
        });
      } else {
        setPmsRx(null);
        setPmsRxLoading(false);
      }
      try {
        const data = crossPharmacy
          ? await searchAllUniversal(query, 10, controller.signal)
          : await searchAll(query, 5, controller.signal);
        if (controller.signal.aborted) return;
        setResults(data);
        setIsLoading(false);

        // Kroll fallback (pharmacy-scoped only): when PHOX has no patient for a
        // name-like query, ask the active pharmacy's Kroll, sync the match into
        // PHOX, and merge it in. This fires once per *settled* query (not per
        // keystroke) because it only runs after the debounced DB search lands
        // empty — so MQTT isn't hit on every partial name.
        if (
          !crossPharmacy &&
          data.patients.length === 0 &&
          (isNameLikeQuery(query) || isPhoneLikeQuery(query))
        ) {
          setKrollFallbackLoading(true);
          try {
            const krollData = await searchKrollFallback(query, 5, controller.signal);
            if (!controller.signal.aborted && krollData.patients.length > 0) {
              setResults((prev) => ({ ...prev, patients: krollData.patients }));
              setKrollFallbackActive(true);
            }
          } catch {
            // Best-effort: a Kroll miss / offline pharmacy leaves the "no
            // results" state as-is. Real errors are logged server-side.
          } finally {
            if (!controller.signal.aborted) setKrollFallbackLoading(false);
          }
        }
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          setError('Search failed');
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [pharmacyId, query, crossPharmacy, debounceMs, reset]);

  return {
    results,
    isLoading,
    pmsRx,
    pmsRxLoading,
    error,
    reset,
    krollFallbackLoading,
    krollFallbackActive,
    // The effective cross-pharmacy mode for the current query — true if the
    // caller flipped the toggle OR the auto-broaden kicked in because the
    // query looks like a unique identifier. Palette uses this to gate the
    // per-row PharmacyBadge so it shows even when the toggle is off but
    // the request actually hit the universal aggregator.
    crossPharmacy,
  };
}
